import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Bot, Wrench, ArrowRight, Search, ChevronDown, ChevronRight, Play, CheckCircle2, BookOpen, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cpFetch } from '@/lib/cpClient';
import type { VolcanoStepTemplate } from '../volcano/types/volcano.types';
import { stepTemplateToNode } from '../volcano/utils/step-template-to-node';

interface NodePaletteProps {
  onAddNode: (nodeType: string, nodeData: any) => void;
  visibility?: 'internal' | 'external' | 'both';
}

interface Agent {
  id: string;
  name: string;
  description?: string;
  domain?: string;
}

interface Tool {
  name: string;
  description?: string;
  server?: string;
}

export default function NodePalette({ onAddNode, visibility = 'both' }: NodePaletteProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [tools, setTools] = useState<Tool[]>([]);
  const [stepTemplates, setStepTemplates] = useState<VolcanoStepTemplate[]>([]);
  const [favorites, setFavorites] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('dc.volcano.stepFavorites') || '[]'); } catch { return []; }
  });
  const [recent, setRecent] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('dc.volcano.stepRecent') || '[]'); } catch { return []; }
  });
  const [expandedSections, setExpandedSections] = useState({
    favorites: true,
    recent: true,
    flow: true,
    stepTemplates: true,
    agricultural: true,
    validation: true,
    agents: true,
    tools: true,
  });

  useEffect(() => {
    loadAgents();
    loadTools();
    loadStepTemplates();
  }, []);

  const loadAgents = async () => {
    try {
      const response = await cpFetch('/api/agents?stage=production,draft');
      if (response.success) {
        setAgents(response.agents || []);
      }
    } catch (error) {
      console.error('Failed to load agents:', error);
    }
  };

  const loadTools = async () => {
    try {
      // Load MCP servers from Volcano (via Orchestrator proxy). Avoid hardcoded mock tool lists.
      const response = await cpFetch('/api/volcano/mcp/servers');
      if (response?.success && Array.isArray(response.servers)) {
        setTools(
          response.servers.map((s: any) => ({
            name: s?.name || s?.id || 'unknown',
            description: s?.description,
            server: s?.url,
          }))
        );
      } else {
        setTools([]);
      }
    } catch (error) {
      console.error('Failed to load tools:', error);
      setTools([]);
    }
  };

  const loadStepTemplates = async () => {
    try {
      const response = await cpFetch(`/api/volcano/steps?scope=all&visibility=${encodeURIComponent(visibility)}`);
      if (response?.success && Array.isArray(response.steps)) {
        setStepTemplates(response.steps);
      } else {
        setStepTemplates([]);
      }
    } catch (error) {
      console.error('Failed to load step templates:', error);
      setStepTemplates([]);
    }
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const trackRecent = (templateId: string) => {
    try {
      const current: string[] = JSON.parse(localStorage.getItem('dc.volcano.stepRecent') || '[]');
      const updated = [templateId, ...current.filter((x) => x !== templateId)].slice(0, 5);
      localStorage.setItem('dc.volcano.stepRecent', JSON.stringify(updated));
      setRecent(updated);
    } catch {
      // ignore
    }
  };

  const removeFavorite = (templateId: string) => {
    const updated = favorites.filter((x) => x !== templateId);
    setFavorites(updated);
    try { localStorage.setItem('dc.volcano.stepFavorites', JSON.stringify(updated)); } catch { /* ignore */ }
  };

  const handleDragStart = (event: React.DragEvent, nodeType: string, nodeData: any) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify({ nodeType, nodeData }));
    event.dataTransfer.effectAllowed = 'move';

    if (nodeData?.stepTemplateId) {
      trackRecent(String(nodeData.stepTemplateId));
    }
  };

  const filteredAgents = agents.filter(agent =>
    agent.name.toLowerCase().includes(search.toLowerCase()) ||
    agent.description?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredTools = tools.filter(tool =>
    tool.name.toLowerCase().includes(search.toLowerCase()) ||
    tool.description?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredStepTemplates = useMemo(() => {
    const q = search.toLowerCase().trim();
    return stepTemplates.filter((t) => {
      if (!q) return true;
      const hay = `${t.name} ${t.description} ${(t.tags || []).join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
  }, [stepTemplates, search]);

  const favoritesList = useMemo(() => {
    const map = new Map(stepTemplates.map((t) => [t.id, t]));
    return favorites.map((id) => map.get(id)).filter(Boolean) as VolcanoStepTemplate[];
  }, [favorites, stepTemplates]);

  const recentList = useMemo(() => {
    const map = new Map(stepTemplates.map((t) => [t.id, t]));
    return recent.map((id) => map.get(id)).filter(Boolean) as VolcanoStepTemplate[];
  }, [recent, stepTemplates]);

  const agriculturalTemplates = useMemo(
    () => stepTemplates.filter((t) => t.category === 'agricultural' || t.flags?.is_agricultural),
    [stepTemplates]
  );

  const validationTemplates = useMemo(
    () => stepTemplates.filter((t) => t.category === 'foundation' && (t.tags || []).some((x) => String(x).toLowerCase().includes('validate'))),
    [stepTemplates]
  );

  return (
    <div className="w-80 bg-white border-r border-gray-200 flex flex-col h-full max-h-full overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <h3 className="font-semibold text-lg mb-2">Node Palette</h3>
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search nodes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4 min-h-0">
        {/* Favorites Section */}
        <div>
          <button
            onClick={() => toggleSection('favorites')}
            className="flex items-center justify-between w-full text-left font-semibold text-sm mb-2 hover:text-purple-600"
          >
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              <span>Favorites</span>
            </div>
            {expandedSections.favorites ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>

          {expandedSections.favorites && (
            <div className="space-y-2 ml-6">
              {favoritesList.length === 0 && (
                <div className="text-xs text-gray-500">Star steps in Volcano Steps to show them here.</div>
              )}
              {favoritesList.map((t) => {
                const { nodeType, nodeData, templateId } = stepTemplateToNode(t);
                const payload = { ...nodeData, stepTemplateId: templateId };
                return (
                  <div
                    key={t.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, nodeType, payload)}
                    className="p-2 bg-purple-50 border border-purple-200 rounded-md cursor-move hover:bg-purple-100 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium truncate">{t.name}</span>
                        {t.flags?.is_agricultural && (
                          <Badge variant="outline" className="text-[10px]">AGR</Badge>
                        )}
                      </div>
                      <button
                        type="button"
                        className="p-1 rounded hover:bg-purple-200"
                        title="Remove from favorites"
                        onClick={(ev) => {
                          ev.preventDefault();
                          ev.stopPropagation();
                          removeFavorite(t.id);
                        }}
                      >
                        <X className="w-3 h-3 text-purple-700" />
                      </button>
                    </div>
                    <div className="text-xs text-gray-600 line-clamp-1">{t.category}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Section */}
        <div>
          <button
            onClick={() => toggleSection('recent')}
            className="flex items-center justify-between w-full text-left font-semibold text-sm mb-2 hover:text-indigo-600"
          >
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              <span>Recent</span>
            </div>
            {expandedSections.recent ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>

          {expandedSections.recent && (
            <div className="space-y-2 ml-6">
              {recentList.length === 0 && (
                <div className="text-xs text-gray-500">Drag steps to build a recent list.</div>
              )}
              {recentList.map((t) => {
                const { nodeType, nodeData, templateId } = stepTemplateToNode(t);
                const payload = { ...nodeData, stepTemplateId: templateId };
                return (
                  <div
                    key={t.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, nodeType, payload)}
                    className="p-2 bg-indigo-50 border border-indigo-200 rounded-md cursor-move hover:bg-indigo-100 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium truncate">{t.name}</span>
                      <button
                        type="button"
                        className="text-xs text-indigo-700 hover:underline"
                        onClick={(ev) => {
                          ev.preventDefault();
                          ev.stopPropagation();
                          navigate(`/agents/steps?stepId=${encodeURIComponent(t.id)}`);
                        }}
                      >
                        Learn
                      </button>
                    </div>
                    <div className="text-xs text-gray-600 line-clamp-1">{t.category}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Validation & Data Section (compact) */}
        <div>
          <button
            onClick={() => toggleSection('validation')}
            className="flex items-center justify-between w-full text-left font-semibold text-sm mb-2 hover:text-amber-600"
          >
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              <span>Validation & Data</span>
            </div>
            {expandedSections.validation ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>

          {expandedSections.validation && (
            <div className="space-y-2 ml-6">
              {validationTemplates.slice(0, 8).map((t) => {
                const { nodeType, nodeData, templateId } = stepTemplateToNode(t);
                const payload = { ...nodeData, stepTemplateId: templateId };
                return (
                  <div
                    key={t.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, nodeType, payload)}
                    className="p-2 bg-amber-50 border border-amber-200 rounded-md cursor-move hover:bg-amber-100 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium truncate">{t.name}</span>
                      <button
                        type="button"
                        className="text-xs text-amber-700 hover:underline"
                        onClick={(ev) => {
                          ev.preventDefault();
                          ev.stopPropagation();
                          navigate(`/agents/steps?stepId=${encodeURIComponent(t.id)}`);
                        }}
                      >
                        Learn
                      </button>
                    </div>
                  </div>
                );
              })}
              {validationTemplates.length === 0 && <div className="text-xs text-gray-500">No validation templates available yet.</div>}
            </div>
          )}
        </div>

        {/* Agricultural Section (compact) */}
        <div>
          <button
            onClick={() => toggleSection('agricultural')}
            className="flex items-center justify-between w-full text-left font-semibold text-sm mb-2 hover:text-green-700"
          >
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              <span>Agricultural</span>
              <Badge variant="outline" className="text-[10px]">AAF</Badge>
            </div>
            {expandedSections.agricultural ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>

          {expandedSections.agricultural && (
            <div className="space-y-2 ml-6">
              {agriculturalTemplates.slice(0, 10).map((t) => {
                const { nodeType, nodeData, templateId } = stepTemplateToNode(t);
                const payload = { ...nodeData, stepTemplateId: templateId };
                return (
                  <div
                    key={t.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, nodeType, payload)}
                    className="p-2 bg-green-50 border border-green-200 rounded-md cursor-move hover:bg-green-100 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium truncate">{t.name}</span>
                      <button
                        type="button"
                        className="text-xs text-green-700 hover:underline"
                        onClick={(ev) => {
                          ev.preventDefault();
                          ev.stopPropagation();
                          navigate(`/agents/steps?stepId=${encodeURIComponent(t.id)}`);
                        }}
                      >
                        Learn
                      </button>
                    </div>
                  </div>
                );
              })}
              {agriculturalTemplates.length === 0 && <div className="text-xs text-gray-500">No agricultural templates available yet.</div>}
            </div>
          )}
        </div>

        {/* Flow Control Section */}
        <div>
          <button
            onClick={() => toggleSection('flow')}
            className="flex items-center justify-between w-full text-left font-semibold text-sm mb-2 hover:text-green-600"
          >
            <div className="flex items-center gap-2">
              <Play className="w-4 h-4" />
              <span>Flow Control</span>
            </div>
            {expandedSections.flow ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>

          {expandedSections.flow && (
            <div className="space-y-2 ml-6">
              <div
                draggable
                onDragStart={(e) => handleDragStart(e, 'start', {
                  label: 'Workflow Start',
                  trigger: 'Manual',
                  description: 'Entry point for the workflow',
                })}
                className="p-3 bg-green-50 border border-green-200 rounded-md cursor-move hover:bg-green-100 transition-colors"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Play className="w-3 h-3 text-green-600" />
                  <span className="text-sm font-medium">Start</span>
                </div>
                <p className="text-xs text-gray-600">Workflow entry point</p>
              </div>

              <div
                draggable
                onDragStart={(e) => handleDragStart(e, 'end', {
                  label: 'Template Created',
                  status: 'success',
                  description: 'Workflow completed successfully',
                })}
                className="p-3 bg-green-50 border border-green-200 rounded-md cursor-move hover:bg-green-100 transition-colors"
              >
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-3 h-3 text-green-600" />
                  <span className="text-sm font-medium">End (Success)</span>
                </div>
                <p className="text-xs text-gray-600">Successful completion</p>
              </div>

              <div
                draggable
                onDragStart={(e) => handleDragStart(e, 'end', {
                  label: 'Pending Approval',
                  status: 'approval',
                  description: 'Awaiting manual approval',
                })}
                className="p-3 bg-orange-50 border border-orange-200 rounded-md cursor-move hover:bg-orange-100 transition-colors"
              >
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-3 h-3 text-orange-600" />
                  <span className="text-sm font-medium">End (Approval)</span>
                </div>
                <p className="text-xs text-gray-600">Requires approval</p>
              </div>
            </div>
          )}
        </div>

        {/* Step Templates Section */}
        <div>
          <button
            onClick={() => toggleSection('stepTemplates')}
            className="flex items-center justify-between w-full text-left font-semibold text-sm mb-2 hover:text-purple-600"
          >
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              <span>Step Templates ({filteredStepTemplates.length})</span>
            </div>
            {expandedSections.stepTemplates ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>

          {expandedSections.stepTemplates && (
            <div className="space-y-2 ml-6">
              {filteredStepTemplates.slice(0, 30).map((t) => {
                const { nodeType, nodeData, templateId } = stepTemplateToNode(t);
                const payload = { ...nodeData, stepTemplateId: templateId };
                return (
                  <div
                    key={t.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, nodeType, payload)}
                    className="p-2 bg-gray-50 border border-gray-200 rounded-md cursor-move hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium truncate">{t.name}</span>
                        {t.flags?.is_new && <Badge className="text-[10px]">NEW</Badge>}
                        {t.flags?.is_popular && <Badge variant="secondary" className="text-[10px]">POPULAR</Badge>}
                        {t.flags?.is_agricultural && <Badge variant="outline" className="text-[10px]">AGR</Badge>}
                      </div>
                      <button
                        type="button"
                        className="text-xs text-gray-700 hover:underline"
                        onClick={(ev) => {
                          ev.preventDefault();
                          ev.stopPropagation();
                          navigate(`/agents/steps?stepId=${encodeURIComponent(t.id)}`);
                        }}
                      >
                        Learn
                      </button>
                    </div>
                    <div className="text-xs text-gray-600 line-clamp-1">{t.category}</div>
                  </div>
                );
              })}
              {filteredStepTemplates.length > 30 && (
                <div className="text-xs text-gray-500">Showing first 30 results. Refine search to narrow down.</div>
              )}
            </div>
          )}
        </div>

        {/* Agents Section */}
        <div>
          <button
            onClick={() => toggleSection('agents')}
            className="flex items-center justify-between w-full text-left font-semibold text-sm mb-2 hover:text-purple-600"
          >
            <div className="flex items-center gap-2">
              <Bot className="w-4 h-4" />
              <span>Agents ({filteredAgents.length})</span>
            </div>
            {expandedSections.agents ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>

          {expandedSections.agents && (
            <div className="space-y-2 ml-6">
              {filteredAgents.map((agent) => (
                <div
                  key={agent.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'agentCall', {
                    agentId: agent.id,
                    agentName: agent.name,
                    agentDescription: agent.description,
                    domain: agent.domain,
                  })}
                  className="p-3 bg-purple-50 border border-purple-200 rounded-md cursor-move hover:bg-purple-100 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Bot className="w-3 h-3 text-purple-600" />
                    <span className="text-sm font-medium">{agent.name}</span>
                  </div>
                  {agent.description && (
                    <p className="text-xs text-gray-600 line-clamp-2">{agent.description}</p>
                  )}
                  {agent.domain && (
                    <Badge variant="secondary" className="text-xs mt-1">
                      {agent.domain}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tools Section */}
        <div>
          <button
            onClick={() => toggleSection('tools')}
            className="flex items-center justify-between w-full text-left font-semibold text-sm mb-2 hover:text-cyan-600"
          >
            <div className="flex items-center gap-2">
              <Wrench className="w-4 h-4" />
              <span>Tools ({filteredTools.length})</span>
            </div>
            {expandedSections.tools ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>

          {expandedSections.tools && (
            <div className="space-y-2 ml-6">
              {filteredTools.map((tool) => (
                <div
                  key={tool.name}
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'toolCall', {
                    toolName: tool.name,
                    toolDescription: tool.description,
                    mcpServer: tool.server,
                  })}
                  className="p-3 bg-cyan-50 border border-cyan-200 rounded-md cursor-move hover:bg-cyan-100 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Wrench className="w-3 h-3 text-cyan-600" />
                    <span className="text-sm font-medium">{tool.name}</span>
                  </div>
                  {tool.description && (
                    <p className="text-xs text-gray-600">{tool.description}</p>
                  )}
                  {tool.server && (
                    <Badge variant="secondary" className="text-xs mt-1">
                      {tool.server}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Input/Output Section */}
        <div>
          <div className="font-semibold text-sm mb-2 flex items-center gap-2">
            <ArrowRight className="w-4 h-4" />
            <span>Input/Output</span>
          </div>
          <div className="space-y-2 ml-6">
            <div
              draggable
              onDragStart={(e) => handleDragStart(e, 'inputOutput', {
                type: 'input',
                label: 'Workflow Input',
              })}
              className="p-3 bg-green-50 border border-green-200 rounded-md cursor-move hover:bg-green-100 transition-colors"
            >
              <div className="flex items-center gap-2 mb-1">
                <ArrowRight className="w-3 h-3 text-green-600" />
                <span className="text-sm font-medium">Input</span>
              </div>
              <p className="text-xs text-gray-600">Define workflow inputs</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

